import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Pagination } from '~/components/Pagination';
import { Pagination2 } from '~/components/Pagination2';
import { useClients } from '~/hooks/clients';
import { clearFormatPhoneNumber } from '~/utils/getPhoneFormatted';

export interface SelectClientsPropsRef {
  get: () => string[];
}

interface SelectClientsProps {
  test?: boolean;
}

const perPage = 200;
let interval: any;

const SelectedClients: React.RefForwardingComponent<
  SelectClientsPropsRef,
  SelectClientsProps
> = ({ test }, ref) => {
  const [data, setData] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  useImperativeHandle(ref, () => ({
    get: () => {
      return data;
    },
  }));

  const { clients, loadClients, isInitialized } = useClients();

  useEffect(() => {
    if (!isInitialized) {
      loadClients();
    }
  }, [loadClients, isInitialized]);

  const filtered = useMemo(() => {
    let count = 0;

    const items =
      search.length === 0
        ? clients.slice((page - 1) * perPage, page * perPage)
        : clients;

    const filteredResult = items.filter(client => {
      let result = true;
      const whatsapp = clearFormatPhoneNumber(client.whatsapp);

      result =
        String(client.name)
          .toLocaleLowerCase()
          .includes(search.toLocaleLowerCase()) || whatsapp.includes(search);

      if (result && count < perPage) {
        count += 1;
        return result;
      }

      return false;
    });

    return filteredResult;
  }, [clients, page, search]);

  const checkedAll = useMemo(() => {
    if (filtered.length === 0) {
      return false;
    }
    return filtered.every(item => data.includes(item.whatsapp));
  }, [filtered, data]);

  const handleSelect = useCallback(
    (whatsappNumber: string) => {
      if (data.includes(whatsappNumber)) {
        const items = [...data];
        items.splice(items.indexOf(whatsappNumber), 1);
        setData(items);
      } else {
        const items = [...data, whatsappNumber];
        setData(items);
      }
    },
    [data],
  );

  const handleCheckAll = useCallback(() => {
    let items = [...data];

    filtered.forEach(item => {
      if (items.includes(item.whatsapp)) {
        items.splice(items.indexOf(item.whatsapp), 1);
        setData(items);
      } else {
        items = [...items, item.whatsapp];
        setData(items);
      }
    });
  }, [filtered, data]);

  const handleChangeSearch = useCallback((value: string) => {
    if (interval) clearInterval(interval);

    interval = setInterval(() => {
      setSearch(value);
    }, 1000);
  }, []);

  return (
    <>
      <h5>{`Selecione os Contatos ${
        data.length > 0 ? `(${data.length})` : ''
      }`}</h5>
      <div className="d-flex justify-between mb-2">
        <div className="form-control-wrap">
          <div className="form-icon form-icon-right">
            <em className="icon ni ni-search" />
          </div>
          <input
            type="text"
            className="form-control"
            id="default-04"
            placeholder="Buscar por contato"
            onChange={event => {
              handleChangeSearch(event.target.value);
            }}
          />
        </div>
        <button
          type="button"
          style={{ background: 'transparent' }}
          onClick={handleCheckAll}
        >
          {checkedAll ? 'Desmarcar Todos' : 'Marcar Todos'}
        </button>
      </div>
      <div className="card card-bordered">
        <div className="card-inner-group">
          <div
            className="card-inner p-0"
            style={{ maxHeight: 500, overflow: 'auto' }}
          >
            <div className="nk-tb-list">
              <div className="nk-tb-item nk-tb-head">
                <div className="nk-tb-col">
                  <span>Nome</span>
                </div>
                <div className="nk-tb-col">
                  <span>Whatsapp</span>
                </div>
                <div className="nk-tb-col" />
              </div>

              {filtered.map(item => (
                <div
                  className="nk-tb-item"
                  key={item.uid}
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSelect(item.whatsapp)}
                >
                  <div className="nk-tb-col">
                    <span className="title">{item.name}</span>
                  </div>
                  <div className="nk-tb-col">
                    <span className="title">{item.whatsapp}</span>
                  </div>
                  <div className="nk-tb-col">
                    {data.includes(item.whatsapp) && (
                      <em
                        className="icon ni ni-check text-success"
                        style={{ fontSize: 24 }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-start mt-2">
        <Pagination2
          page={page}
          next={() => setPage(page + 1)}
          previous={() => {
            if (page > 1) {
              setPage(page - 1);
            }
          }}
        />
      </div>
    </>
  );
};

export default forwardRef(SelectedClients);
